<script setup>
// Builder3 prototype: chatbox-style starter questions -> AI generates one
// plain HTML page (no JSON-schema wrapping, no Vue block registry) with
// data-edit="text|image" markers and a fixed set of --color-* CSS variables.
// The preview iframe is sandboxed (allow-same-origin only, no allow-scripts)
// so nothing the model wrote can execute, while the parent can still reach
// into contentDocument to swap text/images/colors.
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { company, website, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { useTenantImages } from '@/composables/useTenantImages'
import ImagePickerModal from '@/components/builder/ImagePickerModal.vue'

// Same component/route as the AI generator (this file is the shared editor —
// data-edit click-to-swap, colors, save/publish/export — regardless of how
// the page started), entered via ?mode=custom from the sidebar's "Site
// Builder (Custom Code)" entry instead of duplicating this whole view for
// what's really just a different starting action.
const route = useRoute()
const isCustomMode = computed(() => route.query.mode === 'custom')

// Sentinel template_id SiteView.vue looks for to know a published site is a
// raw HTML document (this builder or a pasted-in page) rather than a
// JSON-block/Vue-template site. See src/views/SiteView.vue.
const RAW_HTML_TEMPLATE_ID = 'raw-html'

// Same public-URL scheme DomainView.vue shows contractors: quotastic.ca/site/<company_id>.
const SITE_BASE_URL = (() => {
  const { hostname } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8080'
  return 'https://quotastic.ca'
})()

const COLOR_FIELDS = [
  { key: '--color-primary', label: 'Primary' },
  { key: '--color-secondary', label: 'Secondary' },
  { key: '--color-accent', label: 'Accent' },
  { key: '--color-bg', label: 'Background' },
  { key: '--color-bg-alt', label: 'Background (alt)' },
  { key: '--color-text', label: 'Text' },
]

// ── Starter questions ──────────────────────────────────────────────────
const businessName = ref('')
const colorTheme = ref('')
const notes = ref('')

// Industry is a picklist of whatever industries have a curated design
// reference (see backend/lib/industryStyleBriefs.js), plus "Other" for
// free text — a plain free-text field can't drive a reliable lookup
// ("electrician" vs "electrical contractor" vs "sparky" are the same
// business, three different strings). `industry` stays the human-readable
// value sent to generation either way; `industryKey` is only the stable
// lookup key, sent separately, and only set when a curated option is
// actually selected.
const OTHER_INDUSTRY = '__other__'
const curatedIndustries = ref([]) // [{key, label}] — empty until references exist
const industryKey = ref('')
const industry = ref('')

// "Coming soon" cuts across every industry (any business might want a
// placeholder before its real site is ready) — it's a page type, not
// another industry to add to the picklist above. See COMING_SOON_BRIEF in
// staticSiteGeneration.js for why this bypasses the industry-brief lookup
// and the nav/multi-section hard rules entirely.
const pageType = ref('homepage') // 'homepage' | 'coming_soon'

onMounted(async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ai/static-site/industries`)
    const data = await response.json().catch(() => ({}))
    if (response.ok && data.ok) {
      curatedIndustries.value = data.industries || []
    }
  } catch {
    // Non-critical — the picklist just falls back to "Other" only.
  }
})

function onIndustryPick(key) {
  industryKey.value = key
  if (key === OTHER_INDUSTRY) {
    industry.value = ''
    return
  }
  industry.value = curatedIndustries.value.find((i) => i.key === key)?.label || ''
}

const generating = ref(false)
const elapsedMs = ref(0)
const generatingChars = ref(0)
const error = ref('')
let timerHandle = null

// ── Generated page state ───────────────────────────────────────────────
const html = ref('')
const iframeEl = ref(null)
const generatedAt = ref(null)
const modelUsed = ref('')
const restoredFromPublished = ref(false)
// Diagnostic: whether backend/lib/industryStyleBriefs.js actually injected a
// curated style brief for the last generation — surfaced directly instead of
// inferred from UI state, since "Regenerate"/disabled fields reflect current
// html state either way and don't tell you what the last call actually sent.
const styleBriefApplied = ref(null) // null = unknown/not generated yet, else boolean

// ── Restore last-worked-on site ─────────────────────────────────────────
// `website` (from appConfig) is populated by retrieveWebsite() as soon as
// the company resolves after login, independently of whether Builder3 is
// mounted yet — it may already be filled in by the time this component
// mounts, or arrive shortly after, so this is a reactive watcher rather
// than a one-shot onMounted check. Only raw-HTML sites (this builder's
// content shape) get restored; sites built in the block-based builder are
// left alone since Builder3 can't render that content model.
function parseWebsiteContent(rawContent) {
  if (!rawContent) return {}
  if (typeof rawContent === 'object') return rawContent
  try {
    return JSON.parse(rawContent)
  } catch {
    return {}
  }
}

function hydrateFromLastPublishedSite() {
  if (html.value) return // never clobber an in-progress generation/paste/edit
  if (website.template_id !== RAW_HTML_TEMPLATE_ID) return

  const content = parseWebsiteContent(website.content)
  if (!content.html) return

  html.value = content.html
  businessName.value = businessName.value || website.site_title || ''
  modelUsed.value = content.origin || ''
  restoredFromPublished.value = true
}

watch(() => [website.template_id, website.content], hydrateFromLastPublishedSite, { immediate: true })

// ── Selection / edit panel ─────────────────────────────────────────────
const selected = ref(null) // { key, kind: 'text'|'image', el }
const selectedText = ref('')
const selectedImageSeed = ref('')
const colorValues = ref({})

const canGenerate = computed(() => !generating.value)

// Custom Code and the AI generator share the same saved site (one row per
// company — see hydrateFromLastPublishedSite above), so whichever mode a
// contractor opens, it can load content that was actually made the other
// way. Rather than let AI mode silently treat someone's hand-pasted custom
// code as "existing_html" to edit (or vice versa), each mode only treats
// content as its own when the origin matches — modelUsed already
// distinguishes 'pasted by user' from an actual model name, and is now
// persisted/restored alongside the saved HTML (see save() and
// hydrateFromLastPublishedSite) so this holds across reloads, not just
// within one session. An empty modelUsed (a site saved before this existed)
// is treated as belonging to both modes, so existing sites aren't suddenly
// treated as "not mine" by either builder.
const isPastedOrigin = computed(() => modelUsed.value === 'pasted by user')
const contentBelongsToCurrentMode = computed(() => {
  if (!html.value) return false
  if (!modelUsed.value) return true
  return isCustomMode.value ? isPastedOrigin.value : !isPastedOrigin.value
})

// Once a page exists, business name/industry/color direction are already
// baked into it — editing them here without Start Over wouldn't change
// anything (userPrompt() only feeds `notes` into edit-mode requests, see
// staticSiteGeneration.js), so lock them to avoid the silent no-op. Content
// from the other mode never counts as "editing" here — see
// contentBelongsToCurrentMode above.
const isEditing = computed(() => Boolean(html.value) && contentBelongsToCurrentMode.value)

// True when a site exists but was built with the template builder (a real,
// non-'raw-html' template_id) — hydrateFromLastPublishedSite() above already
// correctly leaves html.value empty rather than trying to load JSON-block
// content as raw HTML, but that alone reads as "nothing built yet" instead
// of "built, just with the other system."
const builtWithBuilder1 = computed(() =>
  !html.value && website.template_id && website.template_id !== RAW_HTML_TEMPLATE_ID
)

const previewEmptyMessage = computed(() => {
  if (builtWithBuilder1.value) {
    return "Your live site was built with Site Builder (Template), not here — this builder can't show or edit it. Switch to Site Builder (Template) to view or edit it — generating or pasting a page here will replace it once saved, not add to it."
  }
  if (!html.value) {
    return isCustomMode.value
      ? 'Paste your HTML and load it to preview it here.'
      : 'Fill in the starter questions and generate a page to preview it here.'
  }
  if (!contentBelongsToCurrentMode.value) {
    return isCustomMode.value
      ? `This site was built with the AI generator (${modelUsed.value}) — there's no custom code here to preview. Paste HTML to start a fresh custom page, or switch to Site Builder (AI) to view or edit the existing one.`
      : "This site was loaded as custom code — there's no AI-generated page here to preview. Click Generate to start a fresh AI page, or switch to Site Builder (Custom Code) to view or edit the existing one."
  }
  return ''
})

function startTimer() {
  const start = Date.now()
  elapsedMs.value = 0
  timerHandle = setInterval(() => {
    elapsedMs.value = Date.now() - start
  }, 250)
}
function stopTimer() {
  if (timerHandle) clearInterval(timerHandle)
  timerHandle = null
}
const elapsedLabel = computed(() => `${(elapsedMs.value / 1000).toFixed(1)}s`)
const generatingProgressLabel = computed(() =>
  generatingChars.value > 0
    ? `${elapsedLabel.value} · ${generatingChars.value.toLocaleString()} chars`
    : elapsedLabel.value
)

// Parses the backend's SSE stream (see aiStaticSite.js) — "progress" events
// update generatingChars so the UI can show something other than a static
// spinner for the 60-130+s a full page can take, "done" carries the final
// result, "error" carries a failure that happened after the response had
// already committed to text/event-stream (so it couldn't just be a normal
// HTTP error status).
async function readGenerationStream(response) {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let eventName = 'message'
  let result = null
  let streamError = null

  let done = false
  while (!done) {
    const chunk = await reader.read()
    done = chunk.done
    if (done) break

    buffer += decoder.decode(chunk.value, { stream: true })
    const lines = buffer.split(/\r?\n/)
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        const payload = JSON.parse(line.slice(5).trim())
        if (eventName === 'progress') {
          generatingChars.value = payload.chars
        } else if (eventName === 'done') {
          result = payload
        } else if (eventName === 'error') {
          streamError = payload.error
        }
        eventName = 'message'
      }
    }
  }

  if (streamError) throw new Error(streamError)
  if (!result) throw new Error('Generation stream ended without a result.')
  return result
}

async function generate() {
  error.value = ''
  selected.value = null
  generating.value = true
  generatingChars.value = 0
  startTimer()

  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/ai/static-site/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: company.company_id,
        business_name: businessName.value,
        industry: industry.value,
        industry_key: industryKey.value && industryKey.value !== OTHER_INDUSTRY ? industryKey.value : undefined,
        color_theme: colorTheme.value,
        page_type: pageType.value !== 'homepage' ? pageType.value : undefined,
        prompt: notes.value,
        // Sending the current page turns this into an edit rather than a
        // fresh generation — see staticSiteGeneration.js userPrompt(). Gate
        // on isEditing, not just html.value: the preview iframe is always
        // mounted (v-show, not v-if), so its contentDocument exists — and
        // captureCurrentHtml() returns a real (if minimal) document's
        // outerHTML — even when no page has been generated yet, so a plain
        // html.value check alone isn't enough. isEditing also excludes
        // content that belongs to the other mode (a pasted page loaded while
        // in AI mode, or vice versa) — Generate on that content should start
        // fresh, not silently edit something this mode didn't create. Once a
        // page does exist and belongs to this mode, still pull from the live
        // iframe DOM (not html.value) so in-progress text/image/color edits
        // aren't silently discarded by the next edit.
        existing_html: isEditing.value ? (captureCurrentHtml() || undefined) : undefined,
      }),
    })

    // Auth/validation failures return a normal JSON error before the backend
    // ever switches to text/event-stream (see aiStaticSite.js) — anything
    // that got as far as streaming is handled by readGenerationStream instead.
    const isStream = (response.headers.get('content-type') || '').includes('text/event-stream')
    const data = isStream ? await readGenerationStream(response) : await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Generation failed (${response.status}).`)
    }

    html.value = data.html
    generatedAt.value = new Date()
    modelUsed.value = data.source?.model || ''
    styleBriefApplied.value = data.source?.styleBriefApplied ?? false
    restoredFromPublished.value = false

    // Not a hard gate — added/removed keys are expected for edits that add
    // or remove a section. This is just a testing-phase signal to catch the
    // model silently dropping/renaming something nobody asked to change.
    if (data.keyDiff?.removed?.length || data.keyDiff?.added?.length) {
      console.warn('[ai:static-site] data-key diff after edit', data.keyDiff)
    }
  } catch (err) {
    error.value = err.message || 'Unable to generate a page.'
  } finally {
    stopTimer()
    generating.value = false
  }
}

// ── Start over ──────────────────────────────────────────────────────────
// Wipes the generated/pasted page and its edit/save/publish state so the
// next Generate call is a fresh generation instead of an edit of whatever
// was here before (see existing_html above). Leaves the starter question
// fields (business name/industry/etc.) alone — they're cheap to keep and
// usually still apply to the next attempt.
function startOver() {
  if (!html.value) return
  if (!window.confirm('Discard the current page and start over? This cannot be undone.')) return

  html.value = ''
  generatedAt.value = null
  modelUsed.value = ''
  styleBriefApplied.value = null
  restoredFromPublished.value = false
  error.value = ''

  selected.value = null
  selectedText.value = ''
  selectedImageSeed.value = ''
  colorValues.value = {}

  pastedHtml.value = ''
  pasteOpen.value = false

  savedUrl.value = ''
  saveError.value = ''
  publishedUrl.value = ''
  publishError.value = ''
  previewUrl.value = ''
  previewError.value = ''
}

// ── Bring your own code ────────────────────────────────────────────────
// Lets a user skip AI generation entirely and drop in an existing page.
// Feeds the exact same preview/edit/publish pipeline as generated output —
// data-edit="text|image" markers and --color-* variables work here too, if
// present in the pasted markup.
const pastedHtml = ref('')
const pasteOpen = ref(false)

function loadPastedHtml() {
  const value = pastedHtml.value.trim()
  if (!value) return

  error.value = ''
  selected.value = null
  savedUrl.value = ''
  saveError.value = ''
  publishedUrl.value = ''
  publishError.value = ''
  previewUrl.value = ''
  previewError.value = ''
  html.value = value
  generatedAt.value = new Date()
  modelUsed.value = 'pasted by user'
  styleBriefApplied.value = null
  restoredFromPublished.value = false
}

// ── Iframe wiring ───────────────────────────────────────────────────────
function onIframeLoad() {
  const doc = iframeEl.value?.contentDocument
  if (!doc) return

  // Seed the color panel from whatever the model declared on :root.
  const rootStyle = doc.defaultView.getComputedStyle(doc.documentElement)
  const values = {}
  for (const { key } of COLOR_FIELDS) {
    values[key] = rootStyle.getPropertyValue(key).trim() || '#000000'
  }
  colorValues.value = values

  doc.addEventListener('click', handleDocClick, true)
}

function handleDocClick(evt) {
  const target = evt.target.closest?.('[data-edit]')
  if (!target) {
    selected.value = null
    return
  }
  evt.preventDefault()
  evt.stopPropagation()

  const kind = target.getAttribute('data-edit')
  const key = target.getAttribute('data-key') || ''
  selected.value = { key, kind, el: target }

  if (kind === 'text') {
    selectedText.value = target.textContent.trim()
  } else if (kind === 'image') {
    selectedImageSeed.value = extractSeed(target.getAttribute('src') || '')
  }

  highlight(target)
}

let highlightedEl = null
function highlight(el) {
  if (highlightedEl) {
    highlightedEl.style.outline = ''
    highlightedEl.style.outlineOffset = ''
  }
  el.style.outline = '2px solid #f26e2e'
  el.style.outlineOffset = '2px'
  highlightedEl = el
}

function extractSeed(src) {
  const match = src.match(/\/seed\/([^/]+)\//)
  return match ? decodeURIComponent(match[1]) : ''
}

function applyTextEdit() {
  if (selected.value?.kind !== 'text') return
  selected.value.el.textContent = selectedText.value
}

function applyImageEdit() {
  if (selected.value?.kind !== 'image') return
  const el = selected.value.el
  const src = el.getAttribute('src') || ''
  const dims = src.match(/\/seed\/[^/]+\/(\d+)\/(\d+)/)
  const w = dims?.[1] || 800
  const h = dims?.[2] || 600
  const seed = encodeURIComponent(selectedImageSeed.value || 'placeholder')
  el.setAttribute('src', `https://picsum.photos/seed/${seed}/${w}/${h}`)
}

// ── Real image upload (Cloudflare Images) ──────────────────────────────
// Same library/upload/delete modal BuilderView.vue uses (ImagePickerModal),
// backed by the same useTenantImages composable PhysicalMarketingView.vue
// uses, instead of a bare file input with no reuse or cleanup.
const {
  images: tenantImages,
  loading: tenantImagesLoading,
  uploading: uploadingImage,
  uploadError: imageUploadError,
  deleting: tenantImagesDeleting,
  load: loadTenantImages,
  upload: uploadTenantImage,
  remove: removeTenantImage,
} = useTenantImages()

const imagePickerOpen = ref(false)

const selectedImageSrc = computed(() =>
  selected.value?.kind === 'image' ? selected.value.el.getAttribute('src') || '' : ''
)

// ── Stock photos (admin-curated, per industry) ─────────────────────────
// Same admin-curated pool generation draws from (see backend/routes/
// stockMedia.js) — surfaced here too so a contractor can swap in another
// curated photo instead of only their own uploads or the picsum placeholder
// the model may have used for a slot with no coverage.
const stockImages = ref([])
const stockImagesLoading = ref(false)

async function loadStockImages() {
  if (!industryKey.value || industryKey.value === OTHER_INDUSTRY) {
    stockImages.value = []
    return
  }
  stockImagesLoading.value = true
  try {
    const token = await getAuthToken()
    const response = await fetch(
      `${API_BASE_URL}/stock-media?industry_key=${encodeURIComponent(industryKey.value)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    )
    const data = await response.json().catch(() => ({}))
    stockImages.value = response.ok && data.ok ? data.images || [] : []
  } catch {
    stockImages.value = []
  } finally {
    stockImagesLoading.value = false
  }
}

function openImagePicker() {
  imagePickerOpen.value = true
  loadTenantImages(company.company_id)
  loadStockImages()
}

function handleImageSelect(image) {
  if (selected.value?.kind !== 'image' || !image?.url) return
  selected.value.el.setAttribute('src', image.url)
}

async function handleImageUpload(file) {
  const uploadedImage = await uploadTenantImage(company.company_id, file)
  if (uploadedImage?.url) {
    handleImageSelect(uploadedImage)
    imagePickerOpen.value = false
  }
}

function handleImageDelete(image) {
  removeTenantImage(image)
}

function applyColor(key) {
  const doc = iframeEl.value?.contentDocument
  if (!doc) return
  doc.documentElement.style.setProperty(key, colorValues.value[key])
}

function deselect() {
  if (highlightedEl) {
    highlightedEl.style.outline = ''
    highlightedEl.style.outlineOffset = ''
  }
  highlightedEl = null
  selected.value = null
}

// Mirrors the sanitization in backend/routes/pagesDeploy.js's
// projectNameForCompany() — the Cloudflare Pages deployment URL for a given
// upload isn't known until after the upload completes, but every project
// also gets a stable production domain of this exact form, so it can be
// predicted ahead of time instead of waiting on the deploy response. Keep
// this in sync if that sanitization ever changes.
function cloudflareProjectDomain(companyId) {
  const safe = String(companyId || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 58)
    .replace(/^-|-$/g, '')

  return `https://qs-${safe || 'site'}.pages.dev`
}

// Fills in what a Builder3 contact form can't know about itself (see the
// "contact form" hard rule in staticSiteGeneration.js): the real API host to
// post to, this tenant's company_id, and the URL to redirect back to after a
// successful submission. Setting .value alone only touches the live DOM
// property; outerHTML serializes the value= *attribute*, so this has to go
// through setAttribute or the stamped value silently wouldn't survive into
// the saved/exported HTML.
function stampFormSubmissionFields(doc, redirectUrl) {
  if (!doc) return

  doc.querySelectorAll('form[action*="/api/form-submission"]').forEach((el) => {
    el.setAttribute('action', `${API_BASE_URL}/form-submission`)
  })

  doc.querySelectorAll('input[type="hidden"][name="company_id"]').forEach((el) => {
    el.setAttribute('value', company.company_id || '')
  })

  doc.querySelectorAll('input[type="hidden"][name="redirect_to"]').forEach((el) => {
    el.setAttribute('value', redirectUrl ? `${redirectUrl}#form-success` : '')
  })
}

// Current document state including any live text/image/color edits made in
// the preview — not just the last-generated/pasted html.value. Pass the URL
// this HTML is about to be served at so any contact form's hidden fields get
// stamped correctly; omit it (e.g. for Export) when there isn't one yet.
function captureCurrentHtml(redirectUrl) {
  const doc = iframeEl.value?.contentDocument
  if (!doc) return html.value

  // The click-to-edit selection highlight (see highlight() above) is an
  // inline style set directly on the live DOM element, not a transient
  // overlay — without stripping it first, whatever's currently selected
  // gets its orange outline baked permanently into every save/publish/
  // preview/export, since they all serialize this same live document.
  if (highlightedEl) {
    highlightedEl.style.outline = ''
    highlightedEl.style.outlineOffset = ''
  }

  stampFormSubmissionFields(doc, redirectUrl)
  const captured = `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`

  if (highlightedEl) {
    highlightedEl.style.outline = '2px solid #f26e2e'
    highlightedEl.style.outlineOffset = '2px'
  }

  return captured
}

// ── Export ───────────────────────────────────────────────────────────
const exportOpen = ref(false)
const exportedHtml = ref('')

function openExport() {
  exportedHtml.value = captureCurrentHtml()
  exportOpen.value = true
}

async function copyExport() {
  try {
    await navigator.clipboard.writeText(exportedHtml.value)
  } catch {
    // Clipboard API may be unavailable (permissions/http) — user can still select+copy manually.
  }
}

// ── Save ─────────────────────────────────────────────────────────────
// Reuses the same /sites/publish endpoint BuilderView.vue (the block-based
// builder) uses, tagged with a sentinel template_id so SiteView.vue renders
// it as a raw HTML document instead of compiling JSON-block content. This is
// the SQL-backed save — it's what makes the page live at publicSiteUrl
// (quotastic.ca/site/<company_id> in prod, localhost:8080 in dev).
const saving = ref(false)
const saveError = ref('')
const savedUrl = ref('')

// Publish (Cloudflare Pages) is a separate, independently-triggered action —
// it deploys whatever's currently in the editor straight to the pages.dev
// mirror and doesn't require Save to have run first. If a save has already
// happened, the backend links the deployment to that site's current
// website_versions row; if not, it still deploys fine, it just has nothing
// to link yet.
const publishing = ref(false)
const publishError = ref('')
const publishedUrl = ref('')

const publicSiteUrl = computed(() =>
  company.company_id ? `${SITE_BASE_URL}/site/${company.company_id}` : ''
)

async function save() {
  if (!html.value || saving.value) return

  if (!company.company_id) {
    saveError.value = 'No company selected — sign in as a contractor to save.'
    return
  }

  saving.value = true
  saveError.value = ''
  savedUrl.value = ''

  try {
    const currentHtml = captureCurrentHtml(publicSiteUrl.value)
    const doc = iframeEl.value?.contentDocument
    const siteTitle = doc?.title?.trim() || businessName.value || 'Untitled site'
    const description =
      doc?.querySelector('meta[name="description"]')?.getAttribute('content') || ''

    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: company.company_id,
        template_id: RAW_HTML_TEMPLATE_ID,
        site_title: siteTitle,
        website: {
          company_id: company.company_id,
          quotastic_domain: website.quotastic_domain || null,
          domain: website.domain || null,
          site_title: siteTitle,
          template_id: RAW_HTML_TEMPLATE_ID,
        },
        content: {
          type: 'raw_html',
          html: currentHtml,
          title: siteTitle,
          description,
          // Persisted so a page reload can tell whether this site belongs to
          // AI mode or Custom Code mode — see contentBelongsToCurrentMode.
          // Without this, that check only worked within the current session.
          origin: modelUsed.value || null,
        },
      }),
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.ok === false) {
      throw new Error(data.error || `Save failed (${response.status}).`)
    }

    savedUrl.value = publicSiteUrl.value
  } catch (err) {
    saveError.value = err.message || 'Unable to save this page.'
  } finally {
    saving.value = false
  }
}

async function publish() {
  if (!html.value || publishing.value) return

  if (!company.company_id) {
    publishError.value = 'No company selected — sign in as a contractor to publish.'
    return
  }

  publishing.value = true
  publishError.value = ''
  publishedUrl.value = ''

  try {
    // Keep the free /site/:id URL in sync with whatever's about to go live
    // on Cloudflare Pages/the custom domain — publishing without ever having
    // clicked Save otherwise leaves the free URL showing stale (or no)
    // content while the paid domain shows the current draft. Abort rather
    // than publish anyway if this fails, so a paying customer's live domain
    // and the free URL can't silently drift apart for a different reason.
    await save()
    if (saveError.value) {
      publishError.value = `Couldn't save before publishing: ${saveError.value}`
      return
    }

    const currentHtml = captureCurrentHtml(cloudflareProjectDomain(company.company_id))
    const doc = iframeEl.value?.contentDocument
    const siteTitle = doc?.title?.trim() || businessName.value || 'Untitled site'

    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/deploy-pages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: company.company_id,
        html: currentHtml,
        title: siteTitle,
      }),
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.ok === false) {
      throw new Error(data.error || `Cloudflare Pages deploy failed (${response.status}).`)
    }

    publishedUrl.value = data.deployment_url || ''
  } catch (err) {
    publishError.value = err.message || 'Unable to publish to Cloudflare Pages.'
  } finally {
    publishing.value = false
  }
}

// ── Preview link ─────────────────────────────────────────────────────
// Snapshots the current draft into its own immutable version row with an
// unguessable share token (see sites.js's /sites/preview route) — unlike
// Save, this never becomes the live site and never touches it, and unlike
// just sending someone the /site/:id link, it keeps showing exactly what it
// showed when shared even if the draft keeps changing afterward.
const previewing = ref(false)
const previewError = ref('')
const previewUrl = ref('')

async function getPreviewLink() {
  if (!html.value || previewing.value) return

  if (!company.company_id) {
    previewError.value = 'No company selected — sign in as a contractor to preview.'
    return
  }

  previewing.value = true
  previewError.value = ''
  previewUrl.value = ''

  try {
    // No redirect_to target is known yet — the preview URL only exists once
    // the backend hands back a token — so this behaves like Export: the
    // contact form still captures the lead correctly (company_id is still
    // stamped), it just won't redirect back to a #form-success banner after
    // a real test submission from this link.
    const currentHtml = captureCurrentHtml()
    const doc = iframeEl.value?.contentDocument
    const siteTitle = doc?.title?.trim() || businessName.value || 'Untitled site'
    const description =
      doc?.querySelector('meta[name="description"]')?.getAttribute('content') || ''

    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: company.company_id,
        template_id: RAW_HTML_TEMPLATE_ID,
        site_title: siteTitle,
        content: {
          type: 'raw_html',
          html: currentHtml,
          title: siteTitle,
          description,
        },
      }),
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.ok === false) {
      throw new Error(data.error || `Preview link failed (${response.status}).`)
    }

    previewUrl.value = `${SITE_BASE_URL}/preview/${data.preview_token}`
  } catch (err) {
    previewError.value = err.message || 'Unable to create a preview link.'
  } finally {
    previewing.value = false
  }
}

async function copyPreviewUrl() {
  try {
    await navigator.clipboard.writeText(previewUrl.value)
  } catch {
    // Clipboard API may be unavailable — user can still select+copy manually.
  }
}

async function copySavedUrl() {
  try {
    await navigator.clipboard.writeText(savedUrl.value)
  } catch {
    // Clipboard API may be unavailable — user can still select+copy manually.
  }
}

// ── Version history ──────────────────────────────────────────────────────
// Every save() already creates a new immutable version row (save() calls
// /sites/publish, whose insertPublishedVersion() always INSERTs, never
// overwrites) — the history was already being captured, nothing surfaced
// it. This is a lightweight safety net, not a full version browser: no
// diffing, no preview-before-restoring, just enough to undo a bad overwrite.
const versionHistoryOpen = ref(false)
const versions = ref([])
const versionsLoading = ref(false)
const versionsError = ref('')
const restoringVersionId = ref(null)

async function loadVersions() {
  versionsLoading.value = true
  versionsError.value = ''
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/versions?company_id=${company.company_id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to load version history.')
    versions.value = data.versions || []
  } catch (err) {
    versionsError.value = err.message || 'Unable to load version history.'
  } finally {
    versionsLoading.value = false
  }
}

function toggleVersionHistory() {
  versionHistoryOpen.value = !versionHistoryOpen.value
  if (versionHistoryOpen.value && !versions.value.length) loadVersions()
}

async function restoreVersion(version) {
  if (!window.confirm(`Restore "${version.site_title || 'this version'}"? This replaces the current live site — you can restore back again afterward if needed.`)) return

  restoringVersionId.value = version.id
  versionsError.value = ''
  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/versions/${version.id}/restore`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) throw new Error(data.error || 'Unable to restore this version.')

    // Reflect the restore in the editor right away rather than leaving the
    // old draft showing while the DB has already moved on.
    const restoredContent = typeof data.content === 'string' ? JSON.parse(data.content) : data.content
    html.value = restoredContent?.html || ''
    businessName.value = data.site_title || businessName.value
    modelUsed.value = restoredContent?.origin || ''
    styleBriefApplied.value = null
    generatedAt.value = null
    restoredFromPublished.value = true
    savedUrl.value = publicSiteUrl.value

    await loadVersions()
  } catch (err) {
    versionsError.value = err.message || 'Unable to restore this version.'
  } finally {
    restoringVersionId.value = null
  }
}
</script>

<template>
  <div class="b3" :class="{ 'b3--no-right-rail': isCustomMode }">
    <aside class="b3-rail b3-rail-left">
      <div class="rail-header">
        <p class="eyebrow">Website builder</p>
        <h1 v-if="isCustomMode">Custom Code</h1>
        <h1 v-else>Builder3 <span class="tag">prototype</span></h1>
        <p v-if="isCustomMode" class="hint">
          Paste in an existing HTML page and edit it with the same
          click-to-swap tools as a generated page.
        </p>
        <p v-else class="hint">
          Plain HTML generation with click-to-swap editing — testing this as an
          alternative to JSON-schema-driven templates.
        </p>
      </div>

      <section v-if="!isCustomMode" class="b3-card">
        <h2>Generate</h2>
        <div class="field">
          <label>Business name</label>
          <input v-model="businessName" :disabled="isEditing" placeholder="e.g. Gardenhub Landscaping" />
        </div>
        <div class="field">
          <label>Page type</label>
          <select v-model="pageType" :disabled="isEditing">
            <option value="homepage">Full homepage</option>
            <option value="coming_soon">Coming soon (placeholder page)</option>
          </select>
        </div>
        <div v-if="pageType === 'homepage'" class="field">
          <label>Industry</label>
          <select :value="industryKey" :disabled="isEditing" @change="onIndustryPick($event.target.value)">
            <option value="" disabled>Select an industry</option>
            <option v-for="opt in curatedIndustries" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            <option :value="OTHER_INDUSTRY">Other (describe)</option>
          </select>
        </div>
        <div v-if="pageType === 'homepage' && industryKey === OTHER_INDUSTRY" class="field">
          <label>Describe the industry</label>
          <input v-model="industry" :disabled="isEditing" placeholder="e.g. landscaping, plumbing, bakery" />
        </div>
        <div class="field">
          <label>Color direction</label>
          <input v-model="colorTheme" :disabled="isEditing" placeholder="e.g. earthy greens, bold and modern" />
        </div>
        <p v-if="isEditing" class="hint">Start over to change the fields above.</p>
        <div class="field">
          <label>{{ isEditing ? 'What would you like to change?' : 'Anything else?' }}</label>
          <textarea
            v-model="notes"
            rows="3"
            :placeholder="isEditing
              ? 'e.g. swap the hero image, add a testimonials section...'
              : 'Pages, tone, sections you want...'"
          />
        </div>

        <button class="btn-primary" :disabled="!canGenerate" @click="generate">
          <span v-if="generating">Generating… {{ generatingProgressLabel }}</span>
          <span v-else>{{ isEditing ? 'Regenerate' : 'Generate page' }}</span>
        </button>

        <p v-if="error" class="error">{{ error }}</p>
        <p v-if="html && !contentBelongsToCurrentMode && !generating" class="hint">
          {{ previewEmptyMessage }}
        </p>
        <p v-if="generatedAt && contentBelongsToCurrentMode && !generating" class="meta">
          Generated in {{ elapsedLabel }} · {{ modelUsed }}
          <template v-if="styleBriefApplied !== null">
            · style brief: {{ styleBriefApplied ? 'applied' : 'not applied' }}
          </template>
        </p>
        <p v-if="restoredFromPublished && contentBelongsToCurrentMode && !generating" class="meta">
          Loaded your last published page.
        </p>
        <button v-if="html && contentBelongsToCurrentMode && !generating" type="button" class="btn-link" @click="startOver">
          Start over
        </button>
      </section>
     
      <section class="b3-card" v-if="isCustomMode" >
        <template v-if="isCustomMode">
          <h2>Paste your HTML</h2>
          <div class="field">
            <label>Business name (optional, used as a fallback title)</label>
            <input v-model="businessName" placeholder="e.g. Gardenhub Landscaping" />
          </div>
        </template>
        <button v-else type="button" class="btn-link" @click="pasteOpen = !pasteOpen">
          {{ pasteOpen ? 'Hide' : 'Have your own HTML? Paste it in' }}
        </button>
        <template v-if="pasteOpen || isCustomMode">
          <div class="field">
            <textarea
              v-model="pastedHtml"
              rows="6"
              class="code"
              placeholder="Paste a full HTML page here..."
            />
          </div>
          <button class="btn-secondary" :disabled="!pastedHtml.trim()" @click="loadPastedHtml">
            Load pasted HTML
          </button>
          <template v-if="isCustomMode">
            <p v-if="html && !contentBelongsToCurrentMode && !generating" class="hint">
              {{ previewEmptyMessage }}
            </p>
            <p v-if="restoredFromPublished && contentBelongsToCurrentMode && !generating" class="meta">
              Loaded your last published page.
            </p>
            <button v-if="html && contentBelongsToCurrentMode && !generating" type="button" class="btn-link" @click="startOver">
              Start over
            </button>
          </template>
        </template>
      </section>

      <section v-if="html && contentBelongsToCurrentMode" class="b3-card">
        <h2>Export, save &amp; publish</h2>
        <div v-if="publicSiteUrl" class="published">
          <p class="meta">Your site:</p>
          <a :href="publicSiteUrl" target="_blank" rel="noopener">{{ publicSiteUrl }}</a>
        </div>
        <button class="btn-secondary" @click="openExport">Export HTML</button>
        <button class="btn-secondary" :disabled="saving" @click="save">
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
        <p v-if="saveError" class="error">{{ saveError }}</p>
        <div v-if="savedUrl" class="published">
          <p class="meta">Saved — live at:</p>
          <a :href="savedUrl" target="_blank" rel="noopener">{{ savedUrl }}</a>
          <button class="btn-link" @click="copySavedUrl">Copy link</button>
        </div>

        <button class="btn-primary" :disabled="publishing" @click="publish">
          {{ publishing ? 'Publishing…' : 'Publish to Cloudflare' }}
        </button>
        <p v-if="publishError" class="error">{{ publishError }}</p>
        <div v-if="publishedUrl" class="published">
          <p class="meta">Published — live on Cloudflare Pages:</p>
          <a :href="publishedUrl" target="_blank" rel="noopener">{{ publishedUrl }}</a>
        </div>

        <button class="btn-secondary" :disabled="previewing" @click="getPreviewLink">
          {{ previewing ? 'Creating link…' : 'Get preview link' }}
        </button>
        <p class="hint">A stable link to send out for feedback — not your live site, and it won't change if you keep editing.</p>
        <p v-if="previewError" class="error">{{ previewError }}</p>
        <div v-if="previewUrl" class="published">
          <p class="meta">Shareable preview:</p>
          <a :href="previewUrl" target="_blank" rel="noopener">{{ previewUrl }}</a>
          <button class="btn-link" @click="copyPreviewUrl">Copy link</button>
        </div>

        <button type="button" class="btn-link" @click="toggleVersionHistory">
          {{ versionHistoryOpen ? 'Hide version history' : 'Version history' }}
        </button>
        <template v-if="versionHistoryOpen">
          <p class="hint">Every save is kept — restore an older one if a recent change wasn't what you wanted.</p>
          <p v-if="versionsError" class="error">{{ versionsError }}</p>
          <p v-if="versionsLoading" class="hint">Loading…</p>
          <ul v-else class="version-list">
            <li v-for="v in versions" :key="v.id" class="version-row">
              <div class="version-row-info">
                <span class="version-row-title">{{ v.site_title || 'Untitled' }}</span>
                <span class="version-row-meta">
                  {{ new Date(v.published_at || v.created_at).toLocaleString() }}
                  <template v-if="v.is_current"> · current</template>
                </span>
              </div>
              <button
                v-if="!v.is_current"
                class="btn-link"
                type="button"
                :disabled="restoringVersionId === v.id"
                @click="restoreVersion(v)"
              >
                {{ restoringVersionId === v.id ? 'Restoring…' : 'Restore' }}
              </button>
            </li>
          </ul>
        </template>
      </section>
    </aside>

    <main class="b3-preview">
      <div v-if="(!html || !contentBelongsToCurrentMode) && !generating" class="empty">
        {{ previewEmptyMessage }}
      </div>
      <div v-if="generating" class="loading">Generating page… {{ generatingProgressLabel }}</div>
      <div class="b3-canvas" v-show="html && contentBelongsToCurrentMode && !generating">
        <iframe
          ref="iframeEl"
          class="frame"
          sandbox="allow-same-origin"
          :srcdoc="html"
          @load="onIframeLoad"
        />
      </div>
    </main>

    <aside class="b3-rail b3-rail-right" v-if="!isCustomMode">
      <section class="b3-card">
        <h2>Colors</h2>
        <div v-if="!html || !contentBelongsToCurrentMode" class="hint">Generate or paste a page to edit its colors.</div>
        <div v-else class="colors">
          <label v-for="c in COLOR_FIELDS" :key="c.key" class="swatch">
            <input
              type="color"
              v-model="colorValues[c.key]"
              @input="applyColor(c.key)"
            />
            {{ c.label }}
          </label>
        </div>
      </section>

      <section class="b3-card">
        <h2>Edit selection</h2>
        <div v-if="!html || !contentBelongsToCurrentMode" class="hint">Generate or paste a page first.</div>
        <div v-else-if="!selected" class="hint">Click any highlighted text or image in the preview.</div>
        <div v-else-if="selected.kind === 'text'" class="field">
          <label>{{ selected.key }}</label>
          <textarea v-model="selectedText" rows="3" @input="applyTextEdit" />
        </div>
        <div v-else-if="selected.kind === 'image'" class="field">
          <label>{{ selected.key }}</label>
          <button class="btn-secondary" @click="openImagePicker">Choose image</button>

          <label>Placeholder seed</label>
          <input v-model="selectedImageSeed" @change="applyImageEdit" />
          <button class="btn-secondary" @click="applyImageEdit">Use placeholder</button>
        </div>
        <button v-if="selected" class="btn-link" @click="deselect">Clear selection</button>
      </section>
    </aside>

    <div v-if="exportOpen" class="modal-backdrop" @click.self="exportOpen = false">
      <div class="modal">
        <div class="modal-head">
          <h3>Exported HTML</h3>
          <button class="btn-link" @click="copyExport">Copy</button>
          <button class="btn-link" @click="exportOpen = false">Close</button>
        </div>
        <textarea readonly class="export-box" :value="exportedHtml" />
      </div>
    </div>

    <ImagePickerModal
      v-model="imagePickerOpen"
      :images="tenantImages"
      :loading="tenantImagesLoading"
      :uploading="uploadingImage"
      :upload-error="imageUploadError"
      :selected-url="selectedImageSrc"
      deletable
      :deleting="tenantImagesDeleting"
      title="Choose image"
      subtitle="Select an uploaded image, or upload a new one."
      :show-stock="Boolean(industryKey && industryKey !== OTHER_INDUSTRY)"
      :stock-images="stockImages"
      :stock-loading="stockImagesLoading"
      @select="handleImageSelect"
      @upload="handleImageUpload"
      @delete="handleImageDelete"
    />
  </div>
</template>

<style scoped>
/* Design tokens/conventions mirrored from SettingsView.vue / DomainView.vue
   (8px radius, var(--surface)/var(--border-subtle) panels, #f26e2e primary,
   #1f7a63 teal accent for eyebrows/links/focus rings) so this sits inside
   the app instead of looking like a separate dark-mode tool. */
.b3 {
  display: grid;
  grid-template-columns: 300px 1fr 320px;
  height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: inherit;
}
/* Custom Code mode hides the right rail (colors / click-to-edit selection).
   Without this override, the grid still reserves its 320px track for a
   child that's no longer there, leaving a blank gap on the right instead of
   the preview expanding to fill the freed-up space. */
.b3--no-right-rail {
  grid-template-columns: 300px 1fr;
}

.b3-rail {
  padding: 20px;
  overflow-y: auto;
  background: var(--surface);
}
.b3-rail-left {
  border-right: 1px solid var(--border-subtle);
}
.b3-rail-right {
  border-left: 1px solid var(--border-subtle);
}

.rail-header {
  margin-bottom: 18px;
}
.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}
.rail-header h1 {
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 850;
  line-height: 1.2;
  color: var(--text-primary);
}
.tag {
  font-size: 10.5px;
  font-weight: 700;
  color: #1f7a63;
  background: #eef8f5;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  vertical-align: middle;
}
.hint {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin: 0;
}

.b3-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(23, 35, 31, 0.05);
}
.b3-card:last-child {
  margin-bottom: 0;
}
.b3-card h2 {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--text-primary);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field label {
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
  color: var(--text-secondary);
}
.field input,
.field select,
.field textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.88rem;
  resize: vertical;
  outline: none;
}
.field textarea {
  min-height: auto;
}
.field input:focus,
.field select:focus,
.field textarea:focus {
  border-color: #9fc8ba;
  box-shadow: 0 0 0 3px rgba(31, 122, 99, 0.1);
}
.field textarea.code {
  font-family: ui-monospace, monospace;
  font-size: 0.78rem;
}

button {
  cursor: pointer;
  font-family: inherit;
}
.btn-primary {
  width: 100%;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-primary);
  color: #fff;
  border: 0;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 800;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-secondary {
  width: 100%;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-alt);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-link {
  align-self: flex-start;
  background: none;
  border: none;
  color: #1f7a63;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.error {
  margin: 0;
  color: var(--accent-danger);
  font-size: 0.78rem;
}
.meta {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.76rem;
}
.published {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.78rem;
}
.published a {
  color: #1f7a63;
  font-weight: 600;
  word-break: break-all;
}

.version-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
}
.version-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
}
.version-row-info {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.version-row-title {
  font-size: 0.82rem;
  font-weight: 700;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.version-row-meta {
  font-size: 0.72rem;
  color: var(--text-secondary);
}

.colors {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.swatch {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: var(--text-primary);
}
.swatch input[type='color'] {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: none;
}

.b3-preview {
  position: relative;
  display: flex;
  background: var(--surface-alt);
  padding: 20px;
}
.b3-canvas {
  flex: 1;
  border-radius: 8px;
  border: 1px solid var(--border-subtle);
  box-shadow: 0 14px 34px rgba(23, 35, 31, 0.08);
  overflow: hidden;
  background: #fff;
}
.empty,
.loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
  text-align: center;
  padding: 40px;
}
.frame {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(23, 35, 31, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: min(800px, 90vw);
  height: min(600px, 80vh);
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 24px 48px rgba(23, 35, 31, 0.16);
  display: flex;
  flex-direction: column;
  padding: 16px;
}
.modal-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.modal-head h3 {
  flex: 1;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--text-primary);
}
.export-box {
  flex: 1;
  width: 100%;
  box-sizing: border-box;
  background: var(--surface-alt);
  color: var(--text-primary);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  padding: 12px;
  font-family: ui-monospace, monospace;
  font-size: 0.76rem;
  resize: none;
}

@media (max-width: 1100px) {
  .b3 {
    grid-template-columns: 260px 1fr 280px;
  }
  .b3--no-right-rail {
    grid-template-columns: 260px 1fr;
  }
}
@media (max-width: 860px) {
  .b3 {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 100vh;
  }
  .b3-rail {
    border: none;
    border-bottom: 1px solid var(--border-subtle);
  }
  .b3-preview {
    min-height: 60vh;
  }
}
</style>
